import { useEffect, useRef } from "react";
import { IconCoins, IconKey, IconShield, IconTrash, IconUser } from "./icons";
import type { MemberVM } from "./memberModel";
import type { MemberActions, MenuState } from "./types";
import { useDialogFocus } from "./useDialogFocus";

type Props = {
  m: MemberVM;
  pos: MenuState;
  onClose: () => void;
  actions: MemberActions;
};

/**
 * Kebab dropdown. Only exposes actions the daemon actually supports — role
 * change, budget, project access, and removal. There is no "suspend" or
 * "resend invite" endpoint, so those design affordances are intentionally
 * omitted. An admin cannot demote or remove their own account.
 */
export function ActionMenu({ m, pos, onClose, actions }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  // Focus the first item on open, trap Tab, close on Escape, restore focus on close.
  useDialogFocus(ref, onClose);

  // Close when clicking outside the menu.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [onClose]);

  const style: React.CSSProperties = {
    top: Math.min(pos.y, window.innerHeight - 220),
    left: Math.min(pos.x, window.innerWidth - 210),
  };

  return (
    <div className="menu" ref={ref} style={style} onClick={(e) => e.stopPropagation()} role="menu">
      <button role="menuitem" onClick={() => actions.open(m.id)}>
        <IconUser /> View profile
      </button>
      {!(m.you && m.role === "admin") && (
        <button
          role="menuitem"
          onClick={() => actions.setRole(m.id, m.role === "admin" ? "member" : "admin")}
        >
          <IconShield /> {m.role === "admin" ? "Demote to member" : "Promote to admin"}
        </button>
      )}
      <button role="menuitem" onClick={() => actions.open(m.id, "budget")}>
        <IconCoins /> Set token budget
      </button>
      <button role="menuitem" onClick={() => actions.open(m.id, "access")}>
        <IconKey /> Project access
      </button>
      {!m.you && (
        <>
          <div className="menu-sep" />
          <button role="menuitem" className="danger" onClick={() => actions.remove(m.id)}>
            <IconTrash /> Remove from workspace
          </button>
        </>
      )}
    </div>
  );
}
